import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  JsonHubProtocol,
  LogLevel
} from '@microsoft/signalr'
import Vue from 'vue';
import { ConnectionStatus, RunStates } from '@/libs/signalR/vuexStore/store/state';
import ACTIONS from './vuexStore/storeActions';

const { connectionHub } = window;

Vue.mixin({
  beforeCreate() {
    const options = this.$options;
    if (options.hub) {
      this.$hub = options.hub;
    } else if (options.parent && options.parent.$hub) {
      this.$hub = options.parent.$hub;
    }
  }
});

const isDev = process.env.NODE_ENV === 'development';

class Hub {
  constructor(store, http, i18n) {
    this.store = store;
    this.http = http;
    this.i18n = i18n;
    this.events = [
      // Wird beim Connecten zum hub ausgegeben
      {
        eventName: 'ConnectEvent',
        // response has to be parsed to be in the form that was provided in the documentation by novacapta
        fn: (response) => {
          console.log('In Hub ConnectEvent', response)
          try {
            const { successful, eventType } = JSON.parse(response);
            store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CONNECT_EVENT, { successful, eventType })
          } catch (err) {
            console.error(err)
          }
        }
      },
      // Wird ausgegeben wenn sich eine Exercise vom Frontent aus ändern (Freigabe oder Entfernung)
      {
        eventName: 'ClientExerciseChangedEvent',
        fn: (response) => {
          console.log('In Hub ClientExerciseChangedEvent', response)
          try {
            const {
              clientId, participantId, exerciseId, eventType
            } = JSON.parse(response)
            store.dispatch(ACTIONS.SIGNAL_R__HANDLE_EXERCISE_CHANGE, {
              clientId, participantId, exerciseId, eventType
            })
          } catch (err) {
            console.error(err)
          }

        }
      },
      // Wird ausgegeben wenn sich Dokumente vom Frontent aus ändern (Freigabe, Entfernung oder alle Entfernt)
      {
        eventName: 'ClientDocumentChangedEvent',
        fn: (response) => {
          console.log('In Hub ClientDocumentChangedEvent', response)
          try {
            const {
              clientId, participantId, exerciseId, documentId, eventType
            } = JSON.parse(response)
            store.dispatch(ACTIONS.SIGNAL_R__HANDLE_DOCUMENT_CHANGE, {
              clientId, participantId, exerciseId, documentId, eventType
            })
          } catch (err) {
            console.error(err);
          }
        }
      },
      // Wird ausgegeben wenn der Run sich ändert.
      {
        eventName: 'ChangedEvent',
        fn: (response) => {
          console.log('In Hub ChangedEvent', response)
          try {
            const { eventType } = JSON.parse(response);
            if (eventType === RunStates.RunFinished) {
              this.close();
            }
            if (this.store.state.signalR.connectionStatus !== ConnectionStatus.Connected) {
              this.start()
            }

            store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CHANGE_EVENT, { eventType })
          } catch (err) {
            console.error(err);
          }
        }
      },
      {
        eventName: 'ObserverTrainingToggleEvent',
        fn: (response) => {
          console.log('ObserverTrainingToggleEvent', response)
          try {
            const { observerTraining, eventType } = JSON.parse(response)
            store.dispatch(ACTIONS.SIGNAL_R__HANDLE_OBSERVERTRAINING, observerTraining)
          } catch (err) {
            console.error(err);
          }
        }
      },
      {
        eventName: 'ParticipantRemovedEvent',
        fn: (response) => {
          console.log('ParticipantRemovedEvent', response);
          try {
            const { eventType, participantId, clientId } = JSON.parse(response);
            if (store.state.user.userId === participantId) {
              store.commit('clearState');
              this.$hub.close();
              this.$router.push('/login')
            }
          } catch (err) {
            console.error(err);
          }
        }
      },
      {
        eventName: 'ObserverRemovedEvent',
        fn: (response) => {
          console.log('ObserverRemovedEvent', response);
          try {
            const { eventType, observerId, clientId } = JSON.parse(response);
            if (store.state.user.userId === observerId) {
              store.commit('clearState');
              this.$hub.close();
              this.$router.push('/login');
            }
          } catch (err) {
            console.error(err);
          }
        }
      },
      {
        eventName: 'ClientParticipantNetworkEvent',
        fn: (response) => {
          console.log('In Hub ClientParticipantNetworkEvent', response)
          try {
            const {
              clientType, clientId, eventType
            } = JSON.parse(response);
            store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CLIENT_PARTICIPANT_NETWORK_EVENT, {
              clientType, clientId, eventType
            })
          } catch (err) {
            console.error(err)
          }
        }
      },
      {
        eventName: 'ClientObserverNetworkEvent',
        fn: ( response ) => {
          console.log('In Hub ClientObserverNetworkEvent', response)
          try {
            const {
              clientType, clientId, eventType
            } = JSON.parse(response);
            store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CLIENT_OBSERVER_NETWORK_EVENT, {
              clientType, clientId, eventType
            })
          } catch (err) {
            console.error(err)
          }
        }
      }
    ];
    this.connection = undefined;
  }

  setup = () => () => {
    if (!this.store.getters.hubToken()) {
      return undefined;
    }
    if (!this.store.getters.runId()) {
      return undefined;
    }
    const options = {
      withCredentials: false,
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
      logMessageContent: isDev,
      logger: isDev ? LogLevel.Warning : LogLevel.Error,
    };
    this.connection = new HubConnectionBuilder()
      .withUrl(`${connectionHub}/hubs/run/${this.store.getters.runId()}/app?identifier=${this.store.getters.hubToken()}`, options)
      .withAutomaticReconnect()
      .withHubProtocol(new JsonHubProtocol())
      .configureLogging(LogLevel.Information)
      .build();
    this.connection.serverTimeoutInMilliseconds = 60000;
    this.connection.onclose((error) => {
      console.assert(this.connection.state === HubConnectionState.Disconnected);
      this.store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CONNECT_EVENT, { successful: this.connection.state === HubConnectionState.Disconnected, eventType: ConnectionStatus.ClientDisconnected })
      if (error) console.log('connection closed. Try refreshing the page', error);
    });
    this.connection.onreconnecting((error) => {
      console.assert(this.connection.state === HubConnectionState.Reconnecting);
      this.store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CONNECT_EVENT, { successful: this.connection.state === HubConnectionState.Reconnecting, eventType: ConnectionStatus.ClientReconnection })
      console.log('reconnecting', error);
    })
    this.connection.onreconnected((connectionId) => {
      console.assert(this.connection.state === HubConnectionState.Connected);
      this.store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CONNECT_EVENT, { successful: this.connection.state === HubConnectionState.Connected, eventType: ConnectionStatus.ClientConnected })
      console.log('connection reestablished');
    })

    this.start();

    this.events.forEach((event) => this.connection.on(event.eventName, event.fn))
    return this.connection;
  }

  async start() {
    if (!this.connection) {
      this.setup()();
      return;
    }
    if (this.connection?.state !== HubConnectionState.Disconnected) {
      return;
    }
    try {
      await this.connection.start();
      console.assert(this.connection.state === HubConnectionState.Connected);
      this.store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CONNECT_EVENT, { successful: this.connection.state === HubConnectionState.Connected, eventType: ConnectionStatus.ClientConnected })
    } catch (err) {
      console.assert(this.connection.state === HubConnectionState.Disconnected);
      this.store.dispatch(ACTIONS.SIGNAL_R__HANDLE_CONNECT_EVENT, { successful: this.connection.state === HubConnectionState.Disconnected, eventType: ConnectionStatus.ClientDisconnected })
      setTimeout(() => this.start(), 5000);
    }
  }

  async close() {
    await this.connection.stop();
  }
}

export default Hub;
